import { get, patch, post } from './coreApi';

export type CoreRevenueSourceType = 'supplier_bill' | 'promostore_order';
export type CoreRevenuePolicySourceType = 'all' | CoreRevenueSourceType;
export type CoreRevenueCounterpartyType =
  | 'supplier'
  | 'decorator'
  | 'shipping-account'
  | 'other-service'
  | 'promostore';

export interface CoreRevenueMeta {
  sourceTypes: CoreRevenueSourceType[];
  policySourceTypes: CoreRevenuePolicySourceType[];
  policyStatuses: Array<'draft' | 'active' | 'paused' | 'ended'>;
  counterpartyTypes: CoreRevenueCounterpartyType[];
  verificationStatuses: Array<'unverified' | 'partial' | 'verified' | 'reversed'>;
  eligibilityStatuses: Array<'observed' | 'pending_verification' | 'eligible' | 'excluded'>;
  revenueStatuses: Array<'observed' | 'accrued' | 'collected' | 'reversed'>;
  observationMode: boolean;
  collectionEnabled: boolean;
}

export interface CoreRevenuePolicy {
  id: number;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'ended';
  mode: 'contracted';
  sourceType: CoreRevenuePolicySourceType;
  counterpartyType?: CoreRevenueCounterpartyType | null;
  counterpartyId?: string | null;
  instanceUniqueId?: string | null;
  accountId?: number | null;
  rateBps: number;
  ratePercent: number;
  includeTax: boolean;
  includeShipping: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  eligibilityConfig?: Record<string, unknown>;
  notes?: string | null;
  createdByUserId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CoreRevenuePolicyInput {
  name: string;
  status?: CoreRevenuePolicy['status'];
  mode?: 'contracted';
  sourceType?: CoreRevenuePolicySourceType;
  counterpartyType?: CoreRevenueCounterpartyType | null;
  counterpartyId?: string | null;
  instanceUniqueId?: string | null;
  accountId?: number | null;
  ratePercent?: number;
  includeTax?: boolean;
  includeShipping?: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  notes?: string | null;
}

export interface CoreRevenueLedgerEvent {
  id: number;
  transactionId: number;
  eventType: string;
  sourceType: CoreRevenueSourceType;
  sourceId: string;
  currency?: string | null;
  previousVerificationStatus?: string | null;
  verificationStatus?: string | null;
  previousEligibilityStatus?: string | null;
  eligibilityStatus?: string | null;
  previousRevenueStatus?: string | null;
  revenueStatus?: string | null;
  previousPolicyId?: number | null;
  policyId?: number | null;
  previousVerifiedAmount: number;
  verifiedAmount: number;
  previousVerifiedEligibleAmount: number;
  verifiedEligibleAmount: number;
  previousFeeAmount: number;
  feeAmount: number;
  feeDelta: number;
  occurredAt: string;
  snapshot?: Record<string, any>;
  metadata?: Record<string, any>;
  createdAt: string;
}

export interface CoreRevenueTransaction {
  id: number;
  sourceType: CoreRevenueSourceType;
  sourceId: string;
  transactionKind: string;
  instanceUniqueId?: string | null;
  accountId?: number | null;
  counterpartyType: CoreRevenueCounterpartyType;
  counterpartyId?: string | null;
  counterpartyName?: string | null;
  currency?: string | null;
  grossAmount: number;
  netAmount: number;
  eligibleAmount: number;
  verifiedAmount: number;
  verifiedEligibleAmount: number;
  taxAmount: number;
  shippingAmount: number;
  verificationStatus: 'unverified' | 'partial' | 'verified' | 'reversed';
  verificationSource?: string | null;
  eligibilityStatus: 'observed' | 'pending_verification' | 'eligible' | 'excluded';
  revenueStatus: 'observed' | 'accrued' | 'collected' | 'reversed';
  policyId?: number | null;
  rateBps?: number | null;
  ratePercent?: number | null;
  feeAmount: number;
  simulatedFee1Percent: number;
  simulatedFee2Percent: number;
  simulatedFee3Percent: number;
  observedAt: string;
  verifiedAt?: string | null;
  sourceUpdatedAt?: string | null;
  evidence?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface CoreRevenueCurrencyBucket {
  currency: string;
  observedGross: number;
  observedNet: number;
  observedEligible: number;
  verifiedEligible: number;
  contractedEligible: number;
  accruedRevenue: number;
  simulated1Percent: number;
  simulated2Percent: number;
  simulated3Percent: number;
  supplierBillNet: number;
  promoStoreVerifiedGmv: number;
}

export interface CoreRevenueCounterpartySummary {
  counterpartyType: CoreRevenueCounterpartyType;
  counterpartyId?: string | null;
  counterpartyName: string;
  currency: string;
  observedEligible: number;
  coveredEligible: number;
  uncoveredEligible: number;
  verifiedEligible: number;
  accruedRevenue: number;
  transactionCount: number;
  policyCoveredCount: number;
  coveragePercent: number;
  simulated1Percent: number;
  simulated2Percent: number;
  simulated3Percent: number;
  uncoveredSimulated1Percent: number;
  uncoveredSimulated2Percent: number;
  uncoveredSimulated3Percent: number;
}

export interface CoreRevenueSummary {
  counts: {
    totalTransactions: number;
    verified: number;
    partial: number;
    unverified: number;
    reversed: number;
    coveredByPolicy: number;
    accrued: number;
    supplierBills: number;
    verifiedSupplierBills: number;
    unverifiedSupplierBills: number;
    promoStoreOrders: number;
    verifiedPromoStoreOrders: number;
    paymentEvidenceConflicts: number;
  };
  policyCount: number;
  activePolicyCount: number;
  currencyBuckets: Record<string, CoreRevenueCurrencyBucket>;
  topCounterparties: CoreRevenueCounterpartySummary[];
  latestObservedAt?: string | null;
}

export interface CoreRevenueIntelligence {
  readOnly: true;
  generatedAt: string;
  mixedCurrency: boolean;
  totals: {
    supplierBillNetObserved: number;
    promoStoreVerifiedGmv: number;
    accruedRevenue: number;
    modeledRevenueAt2Percent: number;
  } | null;
  totalsByCurrency: Record<string, {
    supplierBillNetObserved: number;
    promoStoreVerifiedGmv: number;
    accruedRevenue: number;
    modeledRevenueAt2Percent: number;
  }>;
  priorities: Array<{
    type: string;
    priority: 'high' | 'medium' | 'normal';
    title: string;
    detail: string;
    counterparty?: CoreRevenueCounterpartySummary;
  }>;
  topUnmonetizedCounterparty?: CoreRevenueCounterpartySummary | null;
  summary: string;
}

export interface CoreRevenueSettlementRow {
  policyId: number;
  policyName: string;
  rateBps?: number | null;
  ratePercent?: number | null;
  counterpartyType: string;
  counterpartyId?: string | null;
  counterpartyName: string;
  currency: string;
  grossFees: number;
  adjustments: number;
  netFee: number;
  verifiedEligibleDelta: number;
  eventCount: number;
  transactionCount: number;
  firstEventAt?: string | null;
  lastEventAt?: string | null;
}

export interface CoreRevenueSettlementPreview {
  readOnly: true;
  collectionEnabled: false;
  dateFrom: string;
  dateToExclusive: string;
  totalsByCurrency: Record<string, {
    currency: string;
    grossFees: number;
    adjustments: number;
    netFee: number;
    verifiedEligibleDelta: number;
  }>;
  rows: CoreRevenueSettlementRow[];
}

export interface CoreRevenueSyncResult {
  supplierBills: { scanned: number; created: number; updated: number };
  promoStores: { scanned: number; created: number; updated: number };
  totalScanned: number;
  totalCreated: number;
  totalUpdated: number;
}

export interface CoreRevenuePaymentSyncResult {
  ok: boolean;
  instanceCount: number;
  scanned: number;
  synced: number;
  failed: number;
  needsReview: number;
  results: Array<{
    instanceUniqueId: string;
    ok: boolean;
    scanned?: number;
    synced?: number;
    failed?: number;
    needsReview?: number;
    error?: string;
  }>;
}

export const getCoreRevenueMeta = () =>
  get<CoreRevenueMeta>('/command-center/revenue/meta');

export const syncCoreRevenue = async (full = false): Promise<CoreRevenueSyncResult> => {
  const response = await post<{ result: CoreRevenueSyncResult }>(
    '/command-center/revenue/sync',
    { full }
  );
  return response.result;
};

export const syncCoreRevenueAccountingPayments = async (
  limitPerInstance = 50
): Promise<{ payments: CoreRevenuePaymentSyncResult; ledger: CoreRevenueSyncResult }> => {
  return post<{ payments: CoreRevenuePaymentSyncResult; ledger: CoreRevenueSyncResult }>(
    '/command-center/revenue/sync-accounting-payments',
    { limitPerInstance }
  );
};

export const getCoreRevenueSummary = async (): Promise<CoreRevenueSummary> => {
  const response = await get<{ summary: CoreRevenueSummary }>('/command-center/revenue/summary');
  return response.summary;
};

export const getCoreRevenueIntelligence = async (): Promise<CoreRevenueIntelligence> => {
  const response = await get<{ intelligence: CoreRevenueIntelligence }>(
    '/command-center/revenue/intelligence'
  );
  return response.intelligence;
};

export const getCoreRevenueSettlementPreview = async (params?: {
  dateFrom?: string;
  dateTo?: string;
}): Promise<CoreRevenueSettlementPreview> => {
  const response = await get<{ preview: CoreRevenueSettlementPreview }>(
    '/command-center/revenue/settlement-preview',
    { params }
  );
  return response.preview;
};

export const listCoreRevenueLedgerEvents = async (params?: {
  transactionId?: number;
  sourceType?: CoreRevenueSourceType | '';
  sourceId?: string;
  eventType?: string;
  limit?: number;
  offset?: number;
}): Promise<{ rows: CoreRevenueLedgerEvent[]; total: number; limit: number; offset: number }> => {
  return get('/command-center/revenue/events', { params });
};

export const listCoreRevenueTransactions = async (params?: {
  sourceType?: CoreRevenueSourceType | '';
  verificationStatus?: string;
  eligibilityStatus?: string;
  revenueStatus?: string;
  counterpartyType?: CoreRevenueCounterpartyType | '';
  instanceUniqueId?: string;
  accountId?: number;
  limit?: number;
  offset?: number;
}): Promise<{ rows: CoreRevenueTransaction[]; total: number; limit: number; offset: number }> => {
  return get('/command-center/revenue/transactions', { params });
};

export const listCoreRevenuePolicies = async (): Promise<CoreRevenuePolicy[]> => {
  const response = await get<{ policies?: CoreRevenuePolicy[] }>('/command-center/revenue/policies');
  return Array.isArray(response.policies) ? response.policies : [];
};

export const createCoreRevenuePolicy = async (
  input: CoreRevenuePolicyInput
): Promise<CoreRevenuePolicy> => {
  const response = await post<{ policy: CoreRevenuePolicy }>(
    '/command-center/revenue/policies',
    input
  );
  return response.policy;
};

export const updateCoreRevenuePolicy = async (
  policyId: number,
  input: Partial<CoreRevenuePolicyInput>
): Promise<CoreRevenuePolicy> => {
  const response = await patch<{ policy: CoreRevenuePolicy }>(
    `/command-center/revenue/policies/${policyId}`,
    input
  );
  return response.policy;
};
